// "Zespół i działy" tab: departments and employee mentions.
import { Search } from "lucide-react";
import { useState } from "react";
import {
  Card,
  EmptyState,
  RatingCell,
  SentimentBar,
  SortableTable,
} from "./ui";
import { formatDate, nf, reviewsWord } from "./utils";

const TeamTab = ({ data, onDrill }) => {
  const { departments, employees } = data;
  const [query, setQuery] = useState("");
  const filteredEmployees = employees.filter((e) =>
    e.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <div className="gr-tab">
      <Card
        title="Działy"
        subtitle="Dział przypisany do opinii przez AI – kliknij kartę, aby zobaczyć opinie"
      >
        {departments.length ? (
          <div className="gr-dept-grid">
            {departments.map((d) => (
              <button
                type="button"
                key={d.department}
                className="gr-dept"
                onClick={() => onDrill({ department: d.department })}
              >
                <span className="gr-dept-head">
                  <strong>{d.label}</strong>
                  <span>
                    {nf(d.count)} {reviewsWord(d.count)}
                  </span>
                </span>
                <span className="gr-dept-metrics">
                  <span>
                    <small>Śr. ocena</small>
                    <RatingCell value={d.average_rating} />
                  </span>
                  <span>
                    <small>Sentyment</small>
                    {nf(d.average_sentiment, 1)}/10
                  </span>
                  <span>
                    <small>Negatywne</small>
                    <b
                      className={
                        d.negative_share >= 10 ? "gr-text-negative" : ""
                      }
                    >
                      {nf(d.negative_share, 1)}%
                    </b>
                  </span>
                </span>
                <SentimentBar
                  positive={d.positive_count}
                  neutral={d.neutral_count}
                  negative={d.negative_count}
                  showLegend
                />
                <span className="gr-dept-foot">
                  {d.top_topic && (
                    <span>
                      Główny temat: <b>{d.top_topic}</b>
                    </span>
                  )}
                  {d.top_complaint_theme && (
                    <span className="gr-text-negative">
                      Główna skarga: <b>{d.top_complaint_theme}</b>
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState title="Brak danych o działach" />
        )}
      </Card>

      <Card
        title="Pracownicy wymieniani w opiniach"
        subtitle="Imiona wskazane w treści opinii – kliknij wiersz, aby zobaczyć opinie"
        actions={
          <label className="gr-search gr-search-sm">
            <Search size={14} aria-hidden="true" />
            <input
              type="search"
              placeholder="Szukaj pracownika"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Szukaj pracownika"
            />
          </label>
        }
      >
        <SortableTable
          rowKey={(r) => r.name}
          rows={filteredEmployees}
          initialSort={{ key: "mention_count", dir: "desc" }}
          onRowClick={(r) => onDrill({ employee: r.name })}
          emptyText="Brak wzmianek o pracownikach"
          columns={[
            {
              key: "name",
              label: "Pracownik",
              primary: true,
              render: (r) => (
                <span className="gr-person">
                  <span className="gr-avatar gr-avatar-sm">
                    {r.name.charAt(0)}
                  </span>
                  {r.name}
                </span>
              ),
            },
            {
              key: "mention_count",
              label: "Wzmianki",
              align: "right",
              render: (r) => nf(r.mention_count),
            },
            {
              key: "average_rating",
              label: "Śr. ocena",
              align: "right",
              render: (r) => <RatingCell value={r.average_rating} />,
            },
            {
              key: "negative_mentions",
              label: "Pozyt. / negat.",
              align: "right",
              render: (r) => (
                <span>
                  <span className="gr-text-positive">
                    {r.positive_mentions}
                  </span>{" "}
                  /{" "}
                  <span
                    className={r.negative_mentions ? "gr-text-negative" : ""}
                  >
                    {r.negative_mentions}
                  </span>
                </span>
              ),
            },
            {
              key: "departments",
              label: "Dział",
              sortable: false,
              render: (r) => (
                <span className="gr-muted">{r.departments.join(", ")}</span>
              ),
            },
            {
              key: "top_strength_theme",
              label: "Doceniany za",
              sortable: false,
              render: (r) => r.top_strength_theme || "–",
            },
            {
              key: "last_mention",
              label: "Ostatnio",
              align: "right",
              render: (r) => formatDate(r.last_mention),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default TeamTab;
